import { useCallback, useEffect, useRef, useState } from "react";
import {
	ENVIRONMENT_ROOT_VOLUME_GIB,
	expandEnvironmentRootVolume,
	getEnvironmentRootVolumeOptions,
	type Environment,
	type EnvironmentRootVolumeOptions,
} from "@/lib/api";
import { formatRateCredits } from "@/lib/billing";
import {
	parseExpandedRootVolumeGib,
	rootVolumeCreditsPerHour,
} from "@/lib/environment-root-volume";
import { INPUT_STYLE, focusRingOn, focusRingOff } from "@/lib/form-styles";
import { InputField } from "@/components/common/InputField";
import { ConfirmButton } from "@/components/common/ConfirmButton";

interface ExpandRootVolumeDialogProps {
	open: boolean;
	environment: Environment;
	onClose: () => void;
	onExpanded: (environment: Environment) => void;
}

const CAPTION_STYLE: React.CSSProperties = {
	margin: 0,
	fontSize: "var(--text-caption)",
	color: "var(--fg-2)",
	fontFamily: "var(--font-sans)",
};

/**
 * 環境のルートボリュームを拡張するモーダルダイアログ。EBS は縮小できないため拡張は元に戻せず、
 * 容量を減らすには環境を作り直すしかない。誤操作を防ぐため、取り消せないことの確認チェックを
 * 入れないと実行できないようにする。
 */
export function ExpandRootVolumeDialog({
	open,
	environment,
	onClose,
	onExpanded,
}: ExpandRootVolumeDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [input, setInput] = useState("");
	const [acknowledged, setAcknowledged] = useState(false);
	const [rateOptions, setRateOptions] = useState<EnvironmentRootVolumeOptions | null>(null);
	const [rateOptionsError, setRateOptionsError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setInput(String(environment.rootVolumeGib));
		setAcknowledged(false);
		setRateOptions(null);
		setRateOptionsError(null);
		setSubmitError(null);
		setLoading(false);
		const controller = new AbortController();
		// 単価は判断材料であって拡張の条件ではないので、届かなくても操作は妨げない。
		// ただし課金の増加が分からないまま拡張させないよう、取得の失敗は表示する。
		getEnvironmentRootVolumeOptions(controller.signal)
			.then(setRateOptions)
			.catch((err: unknown) => {
				if (controller.signal.aborted) return;
				setRateOptionsError(
					err instanceof Error ? err.message : "拡張後の単価の取得に失敗しました。",
				);
			});
		return () => controller.abort();
	}, [open, environment.rootVolumeGib]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				// 送信中は「キャンセル」と同様に閉じさせない。閉じても要求は止まらず、
				// 開き直して別の容量を要求すると古い応答が新しい結果を上書きしてしまう。
				if (!loading) onClose();
				e.preventDefault();
			}
		},
		[loading, onClose],
	);

	const parsed = parseExpandedRootVolumeGib(input, environment.rootVolumeGib);
	// 初期値 (現在の容量) のままでは「縮小不可」のエラーを出さない。値を変えてから表示する。
	const showError = input.trim() !== String(environment.rootVolumeGib);
	const canSubmit = parsed.gib !== null && acknowledged;
	const fieldError = (showError ? parsed.error : null) ?? rateOptionsError ?? undefined;
	const rateHint =
		rateOptions !== null && parsed.gib !== null
			? `拡張後は容量分が ${formatRateCredits(rootVolumeCreditsPerHour(parsed.gib, rateOptions.creditsPerGibHour))} クレジット / 時間になります (現在 ${formatRateCredits(rootVolumeCreditsPerHour(environment.rootVolumeGib, rateOptions.creditsPerGibHour))})。`
			: "";

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (parsed.gib === null || !acknowledged) return;
		setSubmitError(null);
		setLoading(true);
		try {
			const expanded = await expandEnvironmentRootVolume(environment.id, parsed.gib);
			onExpanded(expanded);
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="expand-root-volume-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 480,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
						}}
					>
						ルートボリュームを拡張
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					<div
						data-testid="expand-root-volume-warning"
						style={{
							padding: "10px 14px",
							borderRadius: "var(--radius-3)",
							background: "var(--danger-subtle)",
							boxShadow: "0 0 0 1px var(--danger-line)",
							fontSize: "var(--text-ui)",
							lineHeight: "var(--leading-normal)",
							color: "var(--danger)",
							fontFamily: "var(--font-sans)",
						}}
					>
						<strong>この操作は元に戻せません。</strong>
						{
							"拡張後に容量を減らすにはエージェントを削除して作り直す必要があります。拡張後は容量分の課金が増えます (停止中も含む)。また、一度拡張すると約6時間は再度拡張できません。"
						}
					</div>
					<InputField
						label={`新しい容量 (GiB) — 現在 ${environment.rootVolumeGib} GiB`}
						error={fieldError}
					>
						<input
							type="number"
							inputMode="numeric"
							value={input}
							min={environment.rootVolumeGib + 1}
							max={ENVIRONMENT_ROOT_VOLUME_GIB.max}
							step={1}
							onChange={(e) => setInput(e.target.value)}
							style={INPUT_STYLE}
							onFocus={(e) => focusRingOn(e.currentTarget)}
							onBlur={(e) => focusRingOff(e.currentTarget)}
							data-testid="expand-root-volume-gib"
						/>
						<p style={CAPTION_STYLE}>
							{environment.rootVolumeGib + 1} 〜 {ENVIRONMENT_ROOT_VOLUME_GIB.max} GiB。
						</p>
						<p
							style={{ ...CAPTION_STYLE, minHeight: "1.5em", lineHeight: "var(--leading-normal)" }}
							data-testid="expand-root-volume-rate"
						>
							{rateHint}
						</p>
					</InputField>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: "var(--text-ui)",
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
							cursor: "pointer",
							userSelect: "none",
						}}
					>
						<input
							type="checkbox"
							checked={acknowledged}
							onChange={(e) => setAcknowledged(e.target.checked)}
							data-testid="expand-root-volume-acknowledge"
							style={{
								width: 14,
								height: 14,
								margin: 0,
								accentColor: "var(--danger-solid)",
								cursor: "pointer",
							}}
						/>
						拡張は取り消せず、縮小するにはエージェントを作り直す必要があることを理解しました
					</label>
					{submitError && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						variant="destructive"
						label="拡張する"
						loading={loading}
						disabled={!canSubmit}
						data-testid="expand-root-volume-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}
